// v2.0 stub. v1.7 read settings.json / users.json from the OneDrive repo
// via Tauri fs. In v2.0 the server owns configuration:
//   - AI provider + key: in Function App settings (Key Vault-backed).
//   - Users + roles: in Entra (app-role assignments); no client-side lookup.
//   - Prompts: to be exposed via /api/settings when the prompt-library
//     UI lands (planned for v2.1).
// This module remains for signature compatibility with lingering imports;
// each function returns sensible browser-mode defaults with no filesystem
// or Tauri dependency. Delete once no consumer imports it.

export interface LensPrompts {
  vacancy_extraction?: string | null;
  vacancy_analysis?: string | null;
  enrichment?: string | null;
  document_generation?: string | null;
  consolidation?: string | null;
}

export type LlmProvider = "anthropic" | "gemini" | "ollama";

export interface LlmConfig {
  id: string;
  label: string;
  provider: LlmProvider;
  api_key?: string;
  model: string;
  base_url?: string;
  auth_token?: string;
}

export interface LensSettings {
  llm_configs?: LlmConfig[];
  llm_default?: string;
  anthropic_api_key?: string;
  anthropic_model?: string;
  lens_version: string;
  notification_email_enabled: boolean;
  urgency_threshold_days: number;
  prompts?: LensPrompts;
}

export interface LensUser {
  oid: string;
  folder: string;
  display_name: string;
  email: string;
  is_admin: boolean;
  joined: string;
}

// v2.0 fixed default. Real Claude usage is server-side; api_key here is a
// placeholder to satisfy callers that read the field.
const DEFAULT_LLM: LlmConfig = {
  id: "claude",
  label: "Claude",
  provider: "anthropic",
  api_key: "server-managed",
  model: "claude-sonnet-4-20250514",
};

const DEFAULT_SETTINGS: LensSettings = {
  llm_configs: [DEFAULT_LLM],
  llm_default: DEFAULT_LLM.id,
  anthropic_api_key: "server-managed",
  anthropic_model: DEFAULT_LLM.model,
  lens_version: "v2.0",
  notification_email_enabled: false,
  urgency_threshold_days: 5,
  prompts: {},
};

export function resolveLlmConfigs(settings: LensSettings): LlmConfig[] {
  if (settings.llm_configs && settings.llm_configs.length > 0) {
    return settings.llm_configs;
  }
  return [DEFAULT_LLM];
}

export function resolveDefaultLlmId(settings: LensSettings): string {
  const configs = resolveLlmConfigs(settings);
  if (settings.llm_default && configs.some((c) => c.id === settings.llm_default)) {
    return settings.llm_default;
  }
  return configs[0]?.id ?? DEFAULT_LLM.id;
}

export async function readSettings(_repoPath: string): Promise<LensSettings> {
  return DEFAULT_SETTINGS;
}

export async function saveSettings(_repoPath: string, _next: LensSettings): Promise<void> {
  // No-op in v2.0. Settings are managed server-side and won't accept writes
  // from this obsolete path.
  console.warn("saveSettings is a no-op in v2.0 — settings are server-managed");
}

export async function readUsers(_repoPath: string): Promise<LensUser[]> {
  // v2.0 identity is Entra tokens, not a users.json file. Return empty.
  return [];
}

export async function findUserByOid(_repoPath: string, _oid: string): Promise<LensUser | null> {
  return null;
}
